import type z from 'zod/v4'
import { relations, sql } from 'drizzle-orm'
import { index, integer, pgTable, text, timestamp, unique, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { numericAsNumber } from '../../../custom-type'
import { user } from '../../auth'
import { Subject } from '../../content'

// =============================================================================
// LESSON SUBJECT METRICS DAILY - Lookup table (FUTURE SPRINT — scaffolding only)
// -----------------------------------------------------------------------------
// WHY THIS EXISTS
//   The dashboard mockups need "Rata-rata nilai tugas per mapel" — a grade trend
//   broken down by Subject (Bhs Indonesia / Matematika / IPAS ...) and the
//   "Terendah: IPAS - 60,45" summary, at both school and per-child level.
//
//   The existing lookup tables CANNOT serve this:
//     - LessonMetricsDaily.gradeAchieved is per (user, date)        → no subject
//     - LessonHistoryDaily.score        is per (user, enrollment)   → no subject
//       (an enrollment can span lessons of several subjects, so its score can't
//        be split after the fact)
//
//   Grade = earnedXp / possibleXp. To break it down by subject you must aggregate
//   earned and possible XP PER SUBJECT at write time. That is what this table does.
//   A lesson has exactly one Subject (Lesson.subjectId NOT NULL), so a graded
//   lesson's XP lands entirely on that one subject.
//
// GRAIN: one row per (userId, subjectId, date-at-start-of-day-UTC).
//
// HOW TO DERIVE METRICS (read side, still pending):
//   grade for a range, per subject =
//       100 * sum(earnedXp) / NULLIF(sum(possibleXp), 0)      -- 0..100
//   completed lessons for a range, per subject = sum(lessonsCompletedCount).
//   Store RAW earnedXp + possibleXp here (NOT a pre-divided %), because only raw
//   sums aggregate correctly across days/weeks/months. Dividing per-day then
//   averaging would be wrong (Simpson's paradox on unequal day weights).
//
//   TWO DAY ANCHORS: possibleXp/gradedLessonsCount are anchored on the GRADE day (gradedAt)
//   and cover graded lessons only; earnedXp and lessonsCompletedCount are anchored on the
//   COMPLETION day COALESCE(endedAt, gradedAt), and lessonsCompletedCount covers ALL completed
//   lessons of the subject (graded or not — reading / pending-grade included). A row can
//   therefore hold a completion count with 0/0 grade fields (its grade reads as NULL, not 0%).
//
//   Mirrors the XP convention used elsewhere (lesson_metrics_daily, the live leaderboard):
//   earnedXp = sum(EarnedXp.xpEarned). possibleXp is summed
//   PER graded-progress row (same cardinality as earnedXp) — NOT per distinct
//   lesson — so a lesson graded more than once in a day stays a correct weighted
//   average instead of pushing the ratio over 100%.
//
// STATUS: LIVE both ways.
//   WRITE — formula `computeLessonSubjectMetricsDaily` (packages/trigger/src/metrics/), stored for
//   closed days by `rollupUsers`: nightly by cron / rollup-lookup-tables, via `recomputePastDays`
//   after the lesson router's grade + status mutations, and on reset-org-lookup-tables. Today is
//   computed live (`lessonSubjectMetricsDailyToday`).
//   READ — metrics router `getLessonTrend` consumes it: `subjectCompletion` (per-subject nilai =
//   earnedXp/possibleXp; subjects with possibleXp = 0 are excluded so completion-only rows don't
//   read as 0%) plus a per-subject XP breakdown. See lookup-table/README.md.
// =============================================================================

export const LessonSubjectMetricsDaily = pgTable(
  'lesson_subject_metrics_daily',
  {
    id: uuid().defaultRandom().primaryKey(),
    userId: text()
      .references(() => user.id, { onDelete: 'cascade' })
      .notNull(),
    // The subject this row aggregates. A child studying lessons across 3 subjects
    // on one day produces 3 rows for that day.
    subjectId: uuid()
      .references(() => Subject.id, { onDelete: 'cascade' })
      .notNull(),
    date: timestamp({ mode: 'date', withTimezone: true }).notNull(), // normalized to start of day (UTC)

    // Raw XP totals for this subject on this day. Grade = earnedXp / possibleXp.
    // Kept raw (not divided) so range aggregation stays correct — see header note.
    earnedXp: numericAsNumber().notNull().default(0), // sum(EarnedXp.xpEarned) for graded lessons of this subject
    possibleXp: numericAsNumber().notNull().default(0), // sum(LessonToSkillWeight.value) for those lessons

    // Supporting counts (denominators / "X dari Y" style displays). NOTE the two counts use
    // DIFFERENT day anchors, so a lesson can land them on different rows (completed Mon, graded Wed):
    gradedLessonsCount: integer().notNull().default(0), // GRADED lessons on the gradedAt day (matches earnedXp/possibleXp)
    lessonsCompletedCount: integer().notNull().default(0), // ALL completed lessons (graded or not) on the completion (endedAt) day

    // --- Membership snapshot (the "Laporan" dimension) --------------------------------------
    // The school context the learner was in AT ROLL-UP TIME, stamped by the producer on every write.
    // A report ("Laporan baca kelas 1 A") groups on these, so it keeps meaning the members of 1 A in
    // that period even after a student is moved to another class or promoted to the next grade.
    // Every column is nullable: a child who only belongs to a family organization has no school
    // context. A child can be a member of two organizations at once (family + school), which is
    // exactly why the organization is recorded here instead of being re-derived at read time.
    // NOT FOREIGN KEYS, deliberately. A snapshot must carry no referential dependency at all: even
    // `ON DELETE SET NULL` erases the value the moment a school or a class is deleted, which is the
    // history loss the snapshot exists to prevent. Plain text, resolved by the producer via
    // resolveMetricSnapshotContexts (packages/drizzle/src/queries/metric-snapshot-context.ts).
    // Because there is no FK, an id on its own is unrenderable once the row it pointed at is gone,
    // so the labels are stored beside it. They are stamped at roll-up time like everything else
    // here, which means a rebuild re-stamps them too — a renamed class rewrites its own history.
    schoolOrganizationId: text(),
    // The school's name at roll-up time, mirroring organization.name.
    schoolOrganizationName: text(),
    // The "Kelas" / rombel. Always a team of schoolOrganizationId above.
    schoolTeamId: text(),
    // The class label a report prints ("1 A"), mirroring team.name.
    schoolTeamName: text(),
    // The class avatar URL, mirroring team.image.
    schoolTeamImage: text(),
    // The "Tingkat" / grade, mirroring user.schoolClass — a plain text column, not a DB enum.
    // No separate label: it already IS the human-readable value, not an id.
    schoolClass: text(),
    // The learner's own name and avatar at roll-up time, mirroring user.name / user.image.
    // Snapshotted for the same reason as the class label: a learner can be renamed or deleted.
    studentName: text(),
    studentImage: text(),

    // Tracking fields (match the other lookup tables).
    calculatedAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [
    index('idx_lesson_subject_metrics_daily_user').on(t.userId),
    index('idx_lesson_subject_metrics_daily_subject').on(t.subjectId),
    index('idx_lesson_subject_metrics_daily_date').on(t.date),
    index('idx_lesson_subject_metrics_daily_user_date').on(t.userId, t.date),
    // Reporting reads: every row a school produced in a period, then narrowed to one class.
    index('idx_lesson_subject_metrics_daily_school_org_date').on(t.schoolOrganizationId, t.date),
    index('idx_lesson_subject_metrics_daily_school_team_date').on(t.schoolTeamId, t.date),
    // One row per child per subject per day — the upsert conflict target.
    unique('uq_lesson_subject_metrics_daily_user_subject_date').on(t.userId, t.subjectId, t.date),
  ],
)

export const lessonSubjectMetricsDailyRelations = relations(LessonSubjectMetricsDaily, ({ one }) => ({
  user: one(user, {
    fields: [LessonSubjectMetricsDaily.userId],
    references: [user.id],
  }),
  subject: one(Subject, {
    fields: [LessonSubjectMetricsDaily.subjectId],
    references: [Subject.id],
  }),
}))

export const InsertLessonSubjectMetricsDailySchema = createInsertSchema(LessonSubjectMetricsDaily).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

export const UpdateLessonSubjectMetricsDailySchema = createUpdateSchema(LessonSubjectMetricsDaily).partial().omit({
  id: true,
  userId: true,
  subjectId: true,
  date: true,
  createdAt: true,
  updatedAt: true,
})

export const SelectLessonSubjectMetricsDailySchema = createSelectSchema(LessonSubjectMetricsDaily)

export type InsertLessonSubjectMetricsDaily = typeof LessonSubjectMetricsDaily.$inferInsert
export type UpdateLessonSubjectMetricsDaily = z.infer<typeof UpdateLessonSubjectMetricsDailySchema>
export type SelectLessonSubjectMetricsDaily = typeof LessonSubjectMetricsDaily.$inferSelect
